import { LoadingOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Alert, Tag, Tooltip } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillAllLabel,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import {
  postAssessSection,
  useGongAgentAutofill,
} from 'features/libra/stepComponents/GongAgentAutofill'
import { usePrefillBlockedReason } from 'features/libra/stepComponents/prefillGate'
import {
  getEffectiveStrategyType,
  InspectionStrategy,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  ZoneParams,
  ZoneTabPrefillMeta,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  applyTabFragmentWithDerivations,
  buildZoneTabPrefillInputs,
  parseZoneTabFragment,
  runAllTabPrefills,
  ZONE_TAB_SECTION_IDS,
  ZoneCardTabKey,
  ZonePrefillDerivationContext,
  ZoneTabKey,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexPermissionsValues } from 'types'

const PrefillAlert = styled(Alert)`
  margin-bottom: 12px;

  .ant-alert-description {
    font-size: 12px;
  }
`

const BannerSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  .ant-tag {
    white-space: normal;
  }
`

const SummaryLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const TAB_TITLE: Record<ZoneTabKey, string> = {
  basic_info: 'Basic Info',
  design_function: 'Design & Function',
  damage_mechanisms: 'Damage Mechanisms',
  nii_strategy: 'NII Strategy',
  risk_assessment: 'Risk Assessment',
  consequence_of_failure: 'Consequence of Failure',
  zone_breakdown: 'Zone Breakdown Justification',
}

export function formatTabTitles(tabs: readonly ZoneCardTabKey[]): string {
  const titles = tabs.map((tab) => TAB_TITLE[tab])
  if (titles.length <= 1) return titles[0] ?? 'the selected tabs'
  if (titles.length === 2) return `${titles[0]} and ${titles[1]}`
  return `${titles.slice(0, -1).join(', ')}, and ${titles.at(-1)}`
}

export interface ZoneTabAiPrefillBannerProps {
  zone: ZoneParams
  tab: ZoneCardTabKey
  onApply: (updates: Partial<ZoneParams>) => void
  agentPermissions: string[]
  derivationContext?: ZonePrefillDerivationContext
  readonly?: boolean
}

/** Pre-fills one zone-card tab while retaining field-level provenance. */
export const ZoneTabAiPrefillBanner: FC<ZoneTabAiPrefillBannerProps> = ({
  zone,
  tab,
  onApply,
  agentPermissions,
  derivationContext,
  readonly,
}) => {
  const [lastRun, setLastRun] = useState<ZoneTabPrefillMeta | null>(null)
  const [parseFailed, setParseFailed] = useState(false)

  const inputs = useMemo(
    () => buildZoneTabPrefillInputs(zone, tab),
    [zone, tab]
  )

  const onFieldValues = useCallback(
    (fieldValues: FieldValues) => {
      const fragment = parseZoneTabFragment(fieldValues)
      if (!fragment) {
        setParseFailed(true)
        return
      }
      setParseFailed(false)
      // Apply the fragment and re-run the panel-maintained derivations
      // (calculated strategy type + coverage) — single-sourced with the
      // all-tabs runner so the two paths can't drift.
      const updates = applyTabFragmentWithDerivations(
        zone,
        fragment,
        derivationContext
      )
      setLastRun(updates.ai_prefill?.[tab] ?? null)
      onApply(updates)
    },
    [zone, tab, onApply, derivationContext]
  )

  const { autofill, loading, error } = useGongAgentAutofill({
    sectionId: ZONE_TAB_SECTION_IDS[tab],
    onFieldValues,
    inputs,
  })

  const meta = zone.ai_prefill?.[tab] ?? lastRun
  const description = useMemo(() => {
    if (error) return error
    if (parseFailed) return 'The AI response could not be parsed — try again.'
    if (!meta) return undefined
    return (
      <BannerSummary>
        {meta.component_match?.component_name && (
          <SummaryRow>
            <SummaryLabel>Matched component:</SummaryLabel>
            <Tooltip title={meta.component_match.justification ?? undefined}>
              <Tag color='purple'>{meta.component_match.component_name}</Tag>
            </Tooltip>
            <Tag
              color={
                meta.component_match.confidence === 'high'
                  ? 'green'
                  : meta.component_match.confidence === 'medium'
                    ? 'gold'
                    : 'orange'
              }
              style={{ textTransform: 'capitalize' }}>
              {meta.component_match.confidence ?? 'unknown'} confidence
            </Tag>
          </SummaryRow>
        )}
        {meta.rule_notes.map((note) => (
          <SummaryRow key={note}>
            <SummaryLabel>Note:</SummaryLabel>
            <span>{note}</span>
          </SummaryRow>
        ))}
      </BannerSummary>
    )
  }, [error, parseFailed, meta])

  if (readonly) return null

  // The Risk Assessment panel's form is gated on the strategy answers: no
  // strategy → nothing to assess yet; Type C → 100% coverage, no risk factors
  // exist. Mirror those gates here so the button can't fill an invisible form.
  const strategyType =
    tab === 'risk_assessment'
      ? getEffectiveStrategyType(zone.nii_strategy)
      : undefined
  if (tab === 'risk_assessment' && strategyType === InspectionStrategy.C) {
    return null
  }
  const blockedOnStrategy = tab === 'risk_assessment' && !strategyType

  return (
    <AiPrefillGate permissions={agentPermissions as RolodexPermissionsValues[]}>
      <PrefillAlert
        icon={loading ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
        showIcon
        type={error || parseFailed ? 'error' : meta ? 'success' : 'info'}
        message={`${prefillLabel({ loading: false, hasRun: false })} — ${TAB_TITLE[tab]}`}
        description={
          blockedOnStrategy
            ? 'Answer (or pre-fill with AI) the NII Strategy tab first — the strategy type decides which risk factors exist for this zone.'
            : description
        }
        action={
          !loading && (
            <AiPrefillButton
              type='primary'
              size='small'
              icon={<ThunderboltOutlined />}
              onClick={autofill}
              disabled={blockedOnStrategy}
              tooltip={
                blockedOnStrategy
                  ? 'Complete the NII Strategy tab first'
                  : undefined
              }>
              {prefillLabel({ loading, hasRun: meta !== null })}
            </AiPrefillButton>
          )
        }
      />
    </AiPrefillGate>
  )
}

const PrefillAllBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.forecast.background2};
`

const PrefillAllText = styled.div`
  flex: 1;
  min-width: 0;
`

const PrefillAllTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const PrefillAllSubtitle = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export interface ZonePrefillAllBarProps {
  zone: ZoneParams
  tabs: readonly ZoneCardTabKey[]
  onApply: (updates: Partial<ZoneParams>) => void
  agentPermissions: string[]
  derivationContext?: ZonePrefillDerivationContext
  readonly?: boolean
}

/** Pre-fills every visible tab sequentially against one accumulating draft. */
export const ZonePrefillAllBar: FC<ZonePrefillAllBarProps> = ({
  zone,
  tabs,
  onApply,
  agentPermissions,
  derivationContext,
  readonly,
}) => {
  const { GONG_AGENT_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ticketId } = useTicketAccess()
  const [runningTab, setRunningTab] = useState<ZoneCardTabKey | null>(null)
  const [runErrors, setRunErrors] = useState<string[]>([])

  const blockedReason = usePrefillBlockedReason()

  const runAll = useCallback(async () => {
    if (blockedReason) return
    if (!GONG_AGENT_URL) {
      setRunErrors(['GONG_AGENT_URL is not configured'])
      return
    }
    setRunErrors([])
    try {
      const { updates, errors } = await runAllTabPrefills({
        zone,
        tabs,
        derivationContext,
        onProgress: setRunningTab,
        postSection: async (sectionId, inputs) => {
          const { fieldValues } = await postAssessSection(
            axios,
            GONG_AGENT_URL,
            { ticketId, sectionId, inputs }
          )
          return fieldValues
        },
      })
      onApply(updates)
      setRunErrors(
        Object.entries(errors).map(
          ([tab, msg]) => `${TAB_TITLE[tab as ZoneTabKey]}: ${msg}`
        )
      )
    } finally {
      setRunningTab(null)
    }
  }, [
    blockedReason,
    GONG_AGENT_URL,
    axios,
    ticketId,
    zone,
    tabs,
    derivationContext,
    onApply,
  ])

  const hasPrefill = Object.keys(zone.ai_prefill ?? {}).length > 0

  if (readonly) return null

  return (
    <AiPrefillGate permissions={agentPermissions as RolodexPermissionsValues[]}>
      <PrefillAllBar>
        <PrefillAllText>
          <PrefillAllTitle>
            {runningTab
              ? `Pre-filling ${TAB_TITLE[runningTab]} with AI...`
              : 'Pre-fill all tabs with AI'}
          </PrefillAllTitle>
          <PrefillAllSubtitle>
            {runErrors.length > 0
              ? runErrors.join(' · ')
              : `Runs ${formatTabTitles(tabs)} in order; each tab reports its own result.`}
          </PrefillAllSubtitle>
        </PrefillAllText>
        <AiPrefillButton
          type='primary'
          size='small'
          icon={runningTab ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
          onClick={runAll}
          disabled={runningTab !== null}>
          {prefillAllLabel({
            loading: runningTab !== null,
            hasRun: hasPrefill,
          })}
        </AiPrefillButton>
      </PrefillAllBar>
    </AiPrefillGate>
  )
}
